import React, { useMemo, useRef, useState } from "react";
import { ModelListMenu, ModelMark, PickerTrigger } from "../picker";
import type { ModelElementType } from "components/entities";
import { useModelsPolling } from "@/components/main/hooks/useModelsPolling";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import { SpacePickerMenu } from "@/components/main/spaces/SpacePickerMenu";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import {
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";

/** A model a design can make its tables in: Apply makes them as you, so one you edit. */
export const isDesignTarget = (m: ModelElementType) =>
  (m.relation === "Owner" || m.relation === "Collaborator") &&
  !m.is_catalog &&
  !m.scheduled_deletion_date &&
  !!m.space_id;

export function useEditableModels(): ModelElementType[] {
  useModelsPolling();
  const available = useMainStateStore((s) => s.availableModels);
  return useMemo(() => available.filter(isDesignTarget), [available]);
}

/** A space, then a model in it that the caller edits. */
export function SpaceModelPicker({
  models,
  value,
  onChange,
  disabled = false,
}: {
  models: ModelElementType[];
  value: { space: string | null; model: string | null };
  onChange: (value: { space: string | null; model: string | null }) => void;
  disabled?: boolean;
}) {
  const spaceById = useOrgContextStore((s) => s.spaceById);
  const [spaceOpen, setSpaceOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const spaceRef = useRef<HTMLButtonElement>(null);
  const modelRef = useRef<HTMLButtonElement>(null);
  const spaces = useMemo(
    () => [...new Set(models.map((m) => m.space_id as string))],
    [models],
  );
  const space = spaceById(value.space);
  const inSpace = models.filter((m) => m.space_id === value.space);
  const model = models.find((m) => m.model_id === value.model);
  return (
    <div className="grid grid-cols-2 gap-2">
      <PickerTrigger
        ref={spaceRef}
        open={spaceOpen}
        onToggle={() => setSpaceOpen((o) => !o)}
        disabled={disabled}
        lead={space && <SpaceDot color={spaceColor(space)} />}
        label={space?.name}
        placeholder="Pick a space"
      />
      <PickerTrigger
        ref={modelRef}
        open={modelOpen}
        onToggle={() => setModelOpen((o) => !o)}
        disabled={disabled || !space}
        lead={model && <ModelMark model={model} />}
        label={model?.name}
        placeholder="Pick a model"
      />
      <SpacePickerMenu
        anchorRef={spaceRef}
        isOpen={spaceOpen}
        onClose={() => setSpaceOpen(false)}
        selectedSpaceId={value.space}
        eligibleSpaceIds={spaces}
        title="Space"
        emptyLabel="No space holds a model you edit"
        placement="bottom"
        onPick={(id) => {
          setSpaceOpen(false);
          if (id === value.space) return;
          onChange({ space: id, model: null });
          setModelOpen(true);
        }}
      />
      <ModelListMenu
        anchorRef={modelRef}
        isOpen={modelOpen}
        onClose={() => setModelOpen(false)}
        models={inSpace}
        selected={value.model}
        onPick={(id) => {
          onChange({ space: value.space, model: id });
          setModelOpen(false);
        }}
      />
    </div>
  );
}
